import { useContext, useEffect, useRef, useState } from 'react';

import { NotificationContext } from '@client/context/NotificationContext';
import { TNotificationConfig } from '@client/types';
import { logger } from '@client/utils/logger.util';
import { createWorker } from '@client/utils/webworker.util';
import { createSafeReplacer } from '@common/utils';

export const useJson = <T = unknown,>({ json }: { json: T }) => {
  const { notificationApi } = useContext(NotificationContext);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    setLoading(true);
    try {
      if (!workerRef.current) {
        workerRef.current = createWorker();
      }
      const worker = workerRef.current;

      worker.onmessage = (
        e: MessageEvent<{ success: boolean; data: T; error?: string }>,
      ) => {
        const { success, data, error } = e.data;
        if (success) {
          setData(data);
        } else {
          logger.error('Worker error:', error);
          setData(json as T);

          notificationApi?.warning({
            title: 'Worker error occurred',
            description: error || 'Unknown error',
            ...TNotificationConfig,
          });
        }
        setLoading(false);
      };

      worker.onerror = (error) => {
        logger.error('Worker error occurred:', error);
        setData(json as T);
        setLoading(false);

        notificationApi?.warning({
          title: 'Worker error occurred',
          description: error.message,
          ...TNotificationConfig,
        });
      };

      // Pre-serialize the payload to avoid DataCloneError during postMessage
      let payloadString: string;
      try {
        payloadString = JSON.stringify(json, createSafeReplacer());
      } catch (error: any) {
        // As a last resort, fall back to an empty object
        payloadString = '{}';

        notificationApi?.warning({
          title: `Error: ${error.errorType ?? error.name}`,
          description: error.message,
          ...TNotificationConfig,
        });
      }
      worker.postMessage({ __json: payloadString });
    } catch (error: any) {
      logger.error('Preview worker init failed:', error);
      setData(json as T);
      setLoading(false);

      notificationApi?.error({
        title: `Error: ${error.errorType ?? error.name}`,
        description: error.message,
        ...TNotificationConfig,
      });
    }

    return () => {
      // terminate on unmounting of the modal content
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [json, notificationApi]);

  return {
    workerLoading: loading,
    workerData: data as JSON[],
  };
};
